<template>
  <div
    ref="monthListScroller"
    class="calendar-scroll-months"
    data-component="calendar-scroll-date-select"
    :style="[{ height: monthListHeight ? monthListHeight + 'px' : 'auto' }]"
    @scroll="handleScroll"
  >
    <calendar-month
      v-for="month in monthsRendered"
      :id="month.month"
      :key="month.month"
      :month="month.month"
      :days-in-month="month.daysInMonth"
      :input-selected="inputSelected"
      :is-visible="monthsInView.includes(month.month)"
      @month-click="$emit('month-click')"
      @day-selected="$emit('day-selected')"
    />
  </div>
</template>

<script>
  import { mapGetters, mapState, mapActions } from 'vuex'
  import {
    getDate,
    isMonthYearInPast,
    isValidMonthYearFormat,
  } from '@utils/date-helpers'
  import moment from 'moment'
  import CalendarMonth from './calendar-month.vue'

  export default {
    name: 'CalendarMonthList',
    components: {
      CalendarMonth,
    },
    inject: ['localQuery'],
    props: {
      inputSelected: {
        type: String,
        required: true,
      },
      monthListHeight: {
        type: Number,
        required: true,
      },
      monthHeaderHeight: {
        type: Number,
        required: true,
      },
    },
    data: () => ({
      months: [],
      monthsRendered: [],
      monthsFetched: [],
      monthsError: [],
      monthsInView: [],
      timeout: null,
      scrollTimeout: null,
      visibleMonths: [],
    }),
    computed: {
      ...mapState('booking', ['now', 'calendarLoading', 'calendarDates']),
      ...mapState('responsive', { wWidth: 'width' }),
      ...mapGetters('booking', [
        'monthFormat',
        'dateRangeFormat',
        'hotelCalendarDates',
        'dayFormat'
      ]),
      ...mapGetters('hotelData', ['getHotelById']),
      selectedHotel() {
        return this.getHotelById(this.localQuery?.hotelCode)
      },
      firstMonth() {
        const selectedHotelJumpMonth = isValidMonthYearFormat(this.selectedHotel?.jumpMonth)
          && !isMonthYearInPast(this.selectedHotel?.jumpMonth)
          ? this.selectedHotel?.jumpMonth
          : null

        const queryJumpMonth = isValidMonthYearFormat(this.localQuery?.jumpMonth)
          && !isMonthYearInPast(this.localQuery?.jumpMonth)
          ? this.localQuery?.jumpMonth
          : null

        if (selectedHotelJumpMonth) {
          return selectedHotelJumpMonth
        } if (queryJumpMonth) {
          return queryJumpMonth
        }
        return this.now.format(this.monthFormat)
      },
      monthsTouched() {
        return [...this.monthsFetched, ...this.monthsError]
      },
      isMobile() {
        return this.wWidth <= 767
      },
      selectedCheckIn() {
        return this.localQuery.stayDateStart
      },
      monthsData() {
        return this.calendarDates[this.localQuery.hotelCode] || []
      }
    },
    watch: {
      'localQuery.promotionCode': {
        handler(val, oldVal) {
          if (val !== oldVal) {
            this.monthsFetched = []
            this.getInViewMonthData(this.firstMonth)
          }
        },
        deep: true
      },
      calendarLoading(val) {
        if (!val) {
          this.timeout = setTimeout(() => {
            const monthToFetch = this.monthsInView.find(
              (m) => !this.monthsTouched.includes(m)
            )
            if (monthToFetch) {
              this.getInViewMonthData(monthToFetch)
            }
            clearTimeout(this.timeout)
          }, 500)
        }
      },
      selectedCheckIn(val) {
        if (val && this.isMobile) {
          const calendarHeader = document.querySelector('.calendar-scroll-header')
          const stickyHeader = document.querySelector('.calendar-scroll-month-title')
          const scroller = document.querySelector('.calendar-scroll-months')
          const startDay = document.getElementById(val)

          const calendarHeaderHeight = calendarHeader.offsetHeight
          const stickyHeaderHeight = stickyHeader.offsetHeight
          const startDayTopPos = startDay.getBoundingClientRect().top

          const newScrollTopSize = scroller.scrollTop + startDayTopPos - calendarHeaderHeight - stickyHeaderHeight
          scroller.scrollTop = newScrollTopSize
          this.$emit('is-auto-scrolling', true)
        }
      },
    },
    created() {
      this.renderMonths(13)
    },
    mounted() {
      this.months = Array.from(document.querySelectorAll('.calendar-scroll-month'))
      setTimeout(() => {
        this.updateVisibleMonths()
        this.focusOnTodayDate()
      }, 200)
      this.getInViewMonthData(this.firstMonth)
    },
    methods: {
      ...mapActions('booking', ['getCalendarMonthData']),
      getDay(day, month, year) {
        return moment([year, month, day]).format()
      },
      renderMonths(num = 1) {
        const lastMonth = getDate(this.firstMonth, this.monthFormat)
          .subtract(1, 'month')
          .format(this.monthFormat)
        for (let i = 0; num > i; i++) {
          const currentMonth = getDate(lastMonth, this.monthFormat).add(
            i + 1,
            'month'
          )
          const month = currentMonth.month()
          const year = currentMonth.year()
          const daysInMonth = Array(currentMonth.daysInMonth()).fill().map((_, dayIndex) => {
            const day = dayIndex + 1
            const dayFormatted = this.getDay(day, month, year)
            return {
              id: getDate(dayFormatted).format(this.dayFormat),
              day,
              dayFormatted
            }
          })
          this.monthsRendered.push({
            month: currentMonth.format(this.monthFormat),
            daysInMonth
          })
        }
        this.$emit('months-rendered', this.monthsRendered)
      },
      async getInViewMonthData(month) {
        const monthToFetch = month ?? this.monthsInView.find((m) => !this.monthsTouched.includes(m))
        if (!monthToFetch) return
        const currentMonthRangeInfo = this.hotelCalendarDates(
          this.localQuery, monthToFetch
        ).filter((dateRange) => this.filterDateRange(dateRange, monthToFetch))
        if (
          !this.monthsTouched.includes(monthToFetch)
          && currentMonthRangeInfo.length === 0
        ) {
          try {
            await this.getCalendarMonthData({
              calendarType: 'scroll',
              date: monthToFetch,
              customQuery: this.localQuery,
            })
            this.monthsFetched.push(monthToFetch)
          } catch (err) {
            this.monthsError.push(monthToFetch)
          }
        }
      },
      filterDateRange(dateRange, month) {
        const monthInDate = getDate(month, this.monthFormat)
        const startDateMatch = getDate(
          dateRange.startDay,
          this.dateRangeFormat
        ).isSame(monthInDate, 'month')
        const endDateMatch = getDate(
          dateRange.endDay,
          this.dateRangeFormat
        ).isSame(monthInDate, 'month')
        return startDateMatch || endDateMatch
      },
      sortMonthsInView() {
        this.monthsInView.sort((a, b) => {
          const [monthA, yearA] = a.split('-')
          const [monthB, yearB] = b.split('-')

          if (yearA === yearB) {
            return parseInt(monthA) - parseInt(monthB)
          }
          return parseInt(yearA) - parseInt(yearB)
        })
      },
      scrollToItem(month) {
        const monthElement = document.getElementById(month)
        monthElement.scrollIntoView()
        monthElement.focus()
        this.updateVisibleMonths()
      },
      monthIsVisible(element) {
        const rect = element.getBoundingClientRect()
        const containerRect = document
          .querySelector('.calendar-scroll-months')
          .getBoundingClientRect()

        const isVisibleHorizontally = rect.left < containerRect.right && rect.right > containerRect.left
        const isVisibleVertically = rect.top < containerRect.bottom && rect.bottom > containerRect.top

        return (
          isVisibleHorizontally
          && isVisibleVertically
          && getComputedStyle(element).display !== 'none'
          && getComputedStyle(element).visibility !== 'hidden'
          && getComputedStyle(element).opacity !== '0'
        )
      },
      updateVisibleMonths() {
        const visibleMonths = this.months.filter((element) => this.monthIsVisible(element))
        this.monthsInView = visibleMonths.map((el) => el.getAttribute('data-month'))
        this.sortMonthsInView()
        this.$emit('update-month-visible', this.monthsInView[0])
      },
      handleScroll() {
        this.$emit('is-scrolling', true)
        this.$emit('calculate-height')
        clearTimeout(this.scrollTimeout)
        this.scrollTimeout = setTimeout(this.handleScrollStop, 500)
      },
      async handleScrollStop() {
        this.$emit('is-scrolling', false)
        this.$emit('is-auto-scrolling', false)
        this.$emit('calculate-height')
        await this.updateVisibleMonths()

        if (!this.calendarLoading) {
          this.getInViewMonthData()
        }
      },
      focusOnTodayDate() {
        const todayDate = document.getElementById(this.now.format(this.dayFormat))
        if (todayDate) {
          todayDate.focus()
        }
      },
    },
  }
</script>
